import { Component, OnInit } from "@angular/core";
import { DomSanitizer, SafeUrl } from "@angular/platform-browser";

import { SiAuthenticationService } from "@strategic-insights/ng-authentication";
import { keyCloakUtilis as secureUtils} from "../utilis/si-keycloak.utilis";
import { ActivatedRoute, Router } from "@angular/router";
import { keyCloakConfig } from "../models/si-keycloak-config.model";
import { CookieService } from "ngx-cookie";
import { SiKeycloakConfigService } from "src/app/services/si-keycloak-config.service";
import { appResponse } from "../models/si-app-response.model";

@Component({
  selector: "si-keycloak-login",
  templateUrl: "./si-keycloak-login.component.html",
  styleUrls: ["./si-keycloak-login.component.scss"],
})
export class SiKeyCloakLoginComponent implements OnInit {

  constructor(
    private activatedRoute: ActivatedRoute,
    private cookieService: CookieService,
    private siAuthenticationService: SiAuthenticationService,
    private configService: SiKeycloakConfigService,
    private router: Router,
    private sanitizer: DomSanitizer
  ) {}

  /**
   * Validates that a URL is safe to use (only allows http, https, and relative paths)
   * Prevents javascript: and data: protocol attacks
   */
  private isValidUrl(url: string): boolean {
    if (!url || typeof url !== 'string') {
      return false;
    }
    // Allow only http://, https://, and relative paths (starting with /)
    return /^(https?:\/\/|\/[^/])/.test(url.trim());
  }

  /**
   * Validates and sanitizes idpType parameter to prevent XSS
   * Only allows alphanumeric characters, hyphens, and underscores
   */
  private isValidIdpType(idpType: string): boolean {
    if (!idpType || typeof idpType !== 'string') {
      return false;
    }
    // Only allow alphanumeric, hyphens, and underscores
    return /^[a-zA-Z0-9_-]+$/.test(idpType.trim());
  }

  /**
   * Safely navigates to a URL after validation
   */
  private safeNavigateToUrl(url: string): void {
    if (this.isValidUrl(url)) {
      window.location.href = url;
    } else {
      console.error('Invalid URL attempted:', url);
      this.router.navigate(['/unauthorized'], {
        queryParams: {
          getClass: "danger",
          content: "Invalid redirect URL"
        }
      });
    }
  }

  /**
   * Validates error parameter before decoding to prevent XSS
   */
  private isValidBase64(str: string): boolean {
    if (!str || typeof str !== 'string') {
      return false;
    }
    // Basic base64 validation - only allow alphanumeric, +, /, and =
    return /^[A-Za-z0-9+/=]+$/.test(str.trim());
  }

  ngOnInit() {
    this.activatedRoute.queryParams.subscribe(params => {
       let hasError : boolean = false;

       if(params['redirects'] && params['status'] === 'prelogin') {

        const state =  secureUtils.generateRandomString(40);
        const codeVerifier = secureUtils.generateRandomString(128);

        //cookie Store for state & codeVerifier, retrive in keycloaktoken api
        this.cookieService.put('keycloakState',state);
        this.cookieService.put('keycloakCodeVerifier',codeVerifier);

        const originUrl : string = (!this.configService.getConfigByKey('cciOriginUrl')) ? window.location.origin : this.configService.getConfigByKey('cciOriginUrl');

        const keyCloakConfigObj = new keyCloakConfig();
        keyCloakConfigObj.setAuthUrl(this.configService.getConfigByKey('oauthLoginUrl'));
        keyCloakConfigObj.setAuthClientId(this.configService.getConfigByKey('oauthClientId'));
        keyCloakConfigObj.setAuthScope(this.configService.getConfigByKey('oauthScope'));
        keyCloakConfigObj.setAuthResponseType(this.configService.getConfigByKey('oauthResponseType'));
        keyCloakConfigObj.setAuthRedirectUrl(originUrl + this.configService.getConfigByKey('oauthPostLoginRedirectApiEndpoint'));
        keyCloakConfigObj.setAuthCodeChallengeMethod(this.configService.getConfigByKey('oauthCodeChallengeMethod'));


         const redirectUrl = secureUtils.getKeyCloakLoginUrl(keyCloakConfigObj, state, codeVerifier);

         if (params['idpType']) {
           // Validate idpType to prevent XSS injection
           if (this.isValidIdpType(params['idpType'])) {
             const ssoRedirectUrl = redirectUrl + '&kc_idp_hint=' + encodeURIComponent(params['idpType']);
             this.safeNavigateToUrl(ssoRedirectUrl);
           } else {
             console.error('Invalid idpType parameter:', params['idpType']);
             this.router.navigate(['/unauthorized'], {
               queryParams: {
                 getClass: "danger",
                 content: "Invalid identity provider type"
               }
             });
           }
         } else {
           this.safeNavigateToUrl(redirectUrl);
         }

       } else if (params['redirects'] && params['status'] === 'postlogin') {
         let response = this.getAuthResponseFromCookieStore();

         if (response) {
           this.siAuthenticationService.appAuthResponse(response)
             .subscribe((result: boolean) => {
               if (!result) {
                 hasError = true;
                 this.router.navigate(['/unauthorized'], {
                   queryParams: {
                     getClass: "danger",
                     content: "Error Occurred while retriving the App Customers from Response"
                   }
                 });
               }
             });
         } else {
           hasError = true;
           this.router.navigate(['/unauthorized'], {
             queryParams: {
               getClass: "danger",
               content: "Error Occurred while retriving the App Response from Cookie"
             }
           });
         }
       } else if (params['status'] === 'postlogin' && params['error'] && params['code']) {
         hasError = true;
         try {
           // Validate error parameter before decoding to prevent XSS
           if (this.isValidBase64(params['error'])) {
             let decodedError = atob(params['error']);
             this.router.navigate(['/unauthorized'], {
               queryParams: {
                 getClass: "warning",
                 content: decodedError
               }
             });
           } else {
             console.error('Invalid base64 error parameter');
             this.router.navigate(['/unauthorized'], {
               queryParams: {
                 getClass: "danger",
                 content: "Invalid error response format"
               }
             });
           }
         } catch (error) {
           this.router.navigate(['/unauthorized'], {
             queryParams: {
               getClass: "danger",
               content: "Error Occurred while reading the Error Response"
             }
           });
         }
       }
    });
  }

  getAuthResponseFromCookieStore() : any {
     if(this.cookieService.get('openAMToken') && this.cookieService.get('webToken') && this.cookieService.get('openIDMSession') && this.cookieService.get('userName')) {
       return new appResponse(this.cookieService.get('openAMToken'),this.cookieService.get('webToken'),this.cookieService.get('openIDMSession'),this.cookieService.get('userName'),[]);
     } else {
      this.router.navigate(['/unauthorized'], {
        queryParams: {
          getClass: "danger",
          content: "Error Occurred while retriving the App Auth/Web Token from Response"
        }
      });
     }
  }
}
